/**
 * Aircraft type classification with quality gates.
 *
 * Priority (most → least reliable):
 *   1. ICAO type designator from ADS-B (`t` field, e.g. "C172", "EC35", "A320")
 *   2. ADS-B emitter category (A7 = rotorcraft, A1 = light, …)
 *   3. Callsign patterns (rescue helicopter operators etc.)
 *   4. Conservative speed/altitude heuristics (last resort only)
 *
 * The old heuristic classified anything slower than 80 m/s as a helicopter,
 * which caught every Cessna — that's why C172s showed a heli icon.
 */

// Common helicopter ICAO type designators (exact matches).
const HELI_TYPES = new Set([
  // Robinson / Guimbal / Schweizer / Enstrom (trainers, very common in GA)
  'R22', 'R44', 'R66', 'CABR', 'H269', 'S330', 'EN28', 'EN48',
  // Airbus Helicopters (Eurocopter) — incl. rescue helis (H135/H145 = EC35/EC45)
  'EC20', 'EC25', 'EC30', 'EC35', 'EC45', 'EC55', 'EC75', 'H160',
  'AS32', 'AS3B', 'AS50', 'AS55', 'AS65', 'GAZL', 'ALO2', 'ALO3', 'PUMA', 'TIGR',
  // Bell
  'B06', 'B06T', 'B105', 'B212', 'B222', 'B230', 'B407', 'B412', 'B427',
  'B429', 'B430', 'B505',
  // Leonardo / AgustaWestland
  'A109', 'A119', 'A129', 'A139', 'A149', 'A169', 'A189', 'EH10', 'LYNX',
  // Sikorsky / Boeing / MD
  'S61', 'S64', 'S76', 'S92', 'H53', 'H53S', 'H60', 'UH1', 'UH1Y',
  'H500', 'MD52', 'MD60', 'EXPL', 'B47G',
  // Sonstige
  'BK17', 'KA32', 'MI8', 'MI17', 'NH90', 'KMAX', 'W3'
]);

// Common light/GA aircraft designators (exact matches).
// Careful with prefixes: C130 = Hercules, C25A = Citation Jet, C295 = CASA!
const SMALL_TYPES = new Set([
  // Cessna piston singles
  'C120', 'C140', 'C150', 'C152', 'C162', 'C170', 'C172', 'C175', 'C177',
  'C180', 'C182', 'C185', 'C188', 'C195', 'C205', 'C206', 'C207', 'C210',
  'C240', 'C77R', 'C82R', 'T206', 'P210',
  // Piper
  'PA18', 'PA20', 'PA22', 'PA24', 'PA25', 'PA30', 'PA31', 'PA34', 'PA38',
  'PA44', 'PA46', 'P28A', 'P28B', 'P28R', 'P28T', 'P32R', 'P32T', 'P46T',
  // Diamond / Cirrus / Mooney
  'DV20', 'DA40', 'DA42', 'DA50', 'DA62', 'SR20', 'SR22', 'S22T',
  'M20P', 'M20T',
  // Beechcraft Kolben
  'BE19', 'BE23', 'BE24', 'BE33', 'BE35', 'BE36', 'BE55', 'BE58', 'BE76',
  // Robin / Grob / Aquila / Tecnam / sonstige Europa-GA
  'DR40', 'DR30', 'AQUI', 'G115', 'G109', 'TB09', 'TB10', 'TB20', 'TB21',
  'P208', 'P2006', 'P2008', 'P2010', 'TAMP', 'WT9', 'VL3', 'FK9', 'CTSW',
  'CTLS', 'EUPA', 'SIRA', 'Z42', 'Z43', 'Z26',
  // Turboprop-Singles (GA-Nutzung)
  'PC12', 'PC6T', 'TBM7', 'TBM8', 'TBM9', 'KODI', 'EPIC', 'PZL',
  // Ultraleicht/Sonstige generisch
  'ULAC', 'PARA'
]);

// Segelflugzeuge & Motorsegler (TMG) — rund um STR (Schwäbische Alb!) sehr
// häufig. Reine FLARM-Segler ohne Transponder tauchen in den ADS-B-Feeds
// allerdings nicht auf — klassifiziert wird hier alles, was die Feeds liefern.
const GLIDER_TYPES = new Set([
  // Generisch (readsb liefert für viele Segler schlicht "GLID")
  'GLID',
  // Schempp-Hirth
  'DISC', 'DUOD', 'VENT', 'NIMB', 'JANU', 'ARCU', 'ARCE', 'ARCP',
  // Schleicher (ASK/ASW/ASH/ASG)
  'AS20', 'AS21', 'AS22', 'AS24', 'AS25', 'AS26', 'AS28', 'AS29',
  'AS30', 'AS31', 'AS33', 'ASK21',
  // Rolladen-Schneider / DG Flugzeugbau
  'LS4', 'LS6', 'LS7', 'LS8', 'DG40', 'DG80', 'DG1T',
  // Motorsegler / Touring-Motorglider
  'DIMO', 'S10', 'S10S', 'S12', 'TAIF', 'SF25', 'SF28', 'HK36', 'G109'
]);

// Airliner / regional patterns → definitely "airplane".
const AIRPLANE_PATTERNS = [
  /^A(19N|20N|21N|3\d\d|38\d)$/,   // Airbus
  /^B(3\d\w|7\d\d|74\d|78\w)$/,    // Boeing (B38M, B738, B77W, B78X …)
  /^E(1\d\d|19\d|29\d)$/,          // Embraer E-Jets
  /^CRJ\d?$/, /^BCS[13]$/,         // CRJ, A220
  /^AT[47]\d$/, /^DH8\w$/,         // ATR, Dash 8
  /^F(70|100)$/, /^MD1\d$/, /^B46\d$/
];

// Business-Jets → eigene Klasse "jet" (schlanker Rumpf, Hecktriebwerke).
const JET_PATTERNS = [
  /^(C25\w|C56X|C68\w|C700|C750|C500|C501|C510|C525|C550|C560|C650)$/, // Citation
  /^(CL3\d|CL6\d|GLEX|GL5T|GL7T|G\d50|GLF\d)$/, // Challenger / Global / Gulfstream
  /^(E35L|E545|E550|E55P|PC24|FA5X|FA6X|FA7X|FA8X|F2TH|F900|FA50)$/,   // Embraer/Pilatus/Falcon
  /^(LJ\d\d|H25\w|PRM1|HDJT|EA50|SF50|MU30|ASTR|WW24|G280|GALX|BE40|BE4W)$/ // Learjet/Hawker/…
];

// Rescue/police helicopter callsign prefixes (D-A-CH region)
const HELI_CALLSIGN = /^(CHX|DRF|ADAC|HELI|AMB|POLIZEI|PHX|HUMMEL|SAR)/;

/**
 * Classify a flight as 'airplane' | 'jet' | 'helicopter' | 'smallplane' | 'glider'.
 */
export function getAircraftType(flight) {
  const designator = (flight.aircraftType || '').toUpperCase().trim();

  // 1) ICAO-Typ-Designator (zuverlässigste Quelle)
  if (designator) {
    if (HELI_TYPES.has(designator)) return 'helicopter';
    if (GLIDER_TYPES.has(designator)) return 'glider';
    if (SMALL_TYPES.has(designator)) return 'smallplane';
    for (const pattern of JET_PATTERNS) {
      if (pattern.test(designator)) return 'jet';
    }
    for (const pattern of AIRPLANE_PATTERNS) {
      if (pattern.test(designator)) return 'airplane';
    }
  }

  // 2) ADS-B-Emitter-Kategorie
  if (flight.category === 'A7') return 'helicopter';
  if (flight.category === 'A1') return 'smallplane';
  if (flight.category && /^A[2-5]$/.test(flight.category)) return 'airplane';
  if (flight.category === 'B1') return 'glider'; // Segelflugzeug
  if (flight.category === 'B4') return 'smallplane'; // Ultraleicht

  // 3) Callsign-Muster (Rettungs-/Polizeihubschrauber)
  const callsign = (flight.callsign || '').toUpperCase();
  if (HELI_CALLSIGN.test(callsign)) return 'helicopter';

  // 4) Konservative Heuristik als letzter Ausweg:
  //    langsam + niedrig ohne Typinfo → eher Kleinflugzeug (NICHT Heli —
  //    ein schwebender Heli unter 15 m/s ist die einzige Ausnahme)
  const velocity = flight.velocity || 0;
  const altitude = flight.position?.altitude ?? 0;
  if (!flight.onGround && velocity > 0 && velocity < 15 && altitude < 1500) {
    return 'helicopter'; // hovern kann nur ein Heli
  }
  if (velocity > 0 && velocity < 100 && altitude < 4500) {
    return 'smallplane';
  }
  // Oberhalb von FL420 (~12,8 km) fliegen praktisch nur noch Business-Jets
  if (altitude > 12800) {
    return 'jet';
  }
  // Ohne erkannten Typ am Boden (oder langsam & niedrig): fast immer GA/Sport,
  // NIE ein Airliner — Airliner tragen einen erkannten ICAO-Typ und wären oben
  // schon abgefangen worden. Verhindert Riesen-Jet-Icons für stehende Cessnas/
  // Motorsegler ohne Typinfo.
  if (flight.onGround || (velocity < 130 && altitude < 6000)) {
    return 'smallplane';
  }

  return 'airplane';
}

// Friendly names for the info panel (common types around Stuttgart).
const TYPE_NAMES = {
  A319: 'Airbus A319', A320: 'Airbus A320', A20N: 'Airbus A320neo',
  A321: 'Airbus A321', A21N: 'Airbus A321neo', A19N: 'Airbus A319neo',
  A332: 'Airbus A330-200', A333: 'Airbus A330-300', A339: 'Airbus A330-900neo',
  A359: 'Airbus A350-900', A35K: 'Airbus A350-1000', A388: 'Airbus A380',
  B737: 'Boeing 737-700', B738: 'Boeing 737-800', B739: 'Boeing 737-900',
  B38M: 'Boeing 737 MAX 8', B39M: 'Boeing 737 MAX 9',
  B744: 'Boeing 747-400', B748: 'Boeing 747-8', B752: 'Boeing 757-200',
  B763: 'Boeing 767-300', B772: 'Boeing 777-200', B77W: 'Boeing 777-300ER',
  B788: 'Boeing 787-8', B789: 'Boeing 787-9', B78X: 'Boeing 787-10',
  BCS1: 'Airbus A220-100', BCS3: 'Airbus A220-300',
  E190: 'Embraer E190', E195: 'Embraer E195', E290: 'Embraer E190-E2',
  E295: 'Embraer E195-E2', CRJ9: 'Bombardier CRJ900',
  AT76: 'ATR 72-600', DH8D: 'Dash 8 Q400',
  C172: 'Cessna 172 Skyhawk', C152: 'Cessna 152', C182: 'Cessna 182 Skylane',
  P28A: 'Piper PA-28 Cherokee', DA40: 'Diamond DA40', DA42: 'Diamond DA42',
  DV20: 'Diamond DV20 Katana', SR20: 'Cirrus SR20', SR22: 'Cirrus SR22',
  DR40: 'Robin DR400', AQUI: 'Aquila A210', PC12: 'Pilatus PC-12',
  TBM9: 'TBM 900/930/940',
  DIMO: 'Diamond HK36 Super Dimona', S10: 'Stemme S10', SF25: 'Scheibe Falke',
  G109: 'Grob G109', ASK21: 'Schleicher ASK 21', AS21: 'Schleicher ASK 21',
  AS25: 'Schleicher ASH 25', AS33: 'Schleicher AS 33', DISC: 'Schempp-Hirth Discus',
  DUOD: 'Schempp-Hirth Duo Discus', VENT: 'Schempp-Hirth Ventus',
  NIMB: 'Schempp-Hirth Nimbus', ARCU: 'Schempp-Hirth Arcus',
  LS4: 'Rolladen-Schneider LS4', LS8: 'Rolladen-Schneider LS8', GLID: 'Segelflugzeug',
  C25A: 'Citation CJ2', C25B: 'Citation CJ3', C25C: 'Citation CJ4',
  C510: 'Citation Mustang', C525: 'Citation CJ1', C56X: 'Citation XLS',
  C68A: 'Citation Latitude', C700: 'Citation Longitude', C750: 'Citation X',
  CL35: 'Challenger 350', CL60: 'Challenger 604/650',
  GLEX: 'Bombardier Global Express', GL7T: 'Global 7500',
  GLF4: 'Gulfstream IV', GLF5: 'Gulfstream V', GLF6: 'Gulfstream G650',
  E55P: 'Embraer Phenom 300', E545: 'Embraer Legacy 450',
  PC24: 'Pilatus PC-24', F2TH: 'Falcon 2000', FA7X: 'Falcon 7X',
  FA8X: 'Falcon 8X', LJ45: 'Learjet 45', LJ75: 'Learjet 75',
  H25B: 'Hawker 800', PRM1: 'Beechcraft Premier I', SF50: 'Cirrus Vision Jet',
  EC35: 'Airbus H135', EC45: 'Airbus H145', AS50: 'Airbus H125',
  EC30: 'Airbus H130', R44: 'Robinson R44', R66: 'Robinson R66',
  A109: 'Leonardo AW109', A139: 'Leonardo AW139', BK17: 'Airbus BK117',
  CABR: 'Guimbal Cabri G2'
};

/**
 * Human-readable aircraft name from an ICAO designator, or the designator
 * itself if unknown.
 */
export function getAircraftTypeName(designator) {
  const key = (designator || '').toUpperCase().trim();
  if (!key) return null;
  return TYPE_NAMES[key] || key;
}
